/**
 * Yahtzee AI
 *
 * Three difficulty levels:
 * - Easy: Random category selection, random hold decisions
 * - Medium: Greedy - picks highest available score, holds matching dice
 * - Hard: Strategic - considers expected value, upper section bonus tracking,
 *         holds optimally for best categories, prioritizes Yahtzee and straights
 */

import {
  Die,
  DieValue,
  ScoreCategory,
  YahtzeeDifficulty,
  YahtzeePlayer,
  ALL_CATEGORIES,
  UPPER_CATEGORIES,
  UPPER_BONUS_THRESHOLD,
  NUM_DICE,
} from './types';
import { YahtzeeEngine } from './YahtzeeEngine';

export interface AIDecision {
  holdIndices: number[];
  category: ScoreCategory | null; // null means AI wants to roll again
}

export class YahtzeeAI {
  private difficulty: YahtzeeDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: YahtzeeDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  setDifficulty(difficulty: YahtzeeDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): YahtzeeDifficulty {
    return this.difficulty;
  }

  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 800;
      case 'medium': return 1200;
      case 'hard': return 1800;
      default: return 1000;
    }
  }

  scheduleAction(onAction: () => void, customDelay?: number): () => void {
    this.cancelPending();
    const delay = customDelay ?? this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      onAction();
    }, delay);

    return () => this.cancelPending();
  }

  cancelPending(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPending();
  }

  // =========================================================================
  // Decision Making
  // =========================================================================

  /**
   * Decide which dice to hold before rolling.
   * Returns indices of dice to hold.
   */
  decideHolds(
    dice: Die[],
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): number[] {
    switch (this.difficulty) {
      case 'easy': return this.decideHoldsEasy(dice);
      case 'medium': return this.decideHoldsMedium(dice, player, engine);
      case 'hard': return this.decideHoldsHard(dice, player, engine);
      default: return [];
    }
  }

  /**
   * Decide which category to score.
   * Returns the chosen category.
   */
  decideCategory(
    dice: Die[],
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): ScoreCategory {
    const available = engine.getAvailableCategories(player);
    if (available.length === 0) {
      return ALL_CATEGORIES[0]; // Should not happen
    }

    switch (this.difficulty) {
      case 'easy': return this.decideCategoryEasy(available);
      case 'medium': return this.decideCategoryMedium(dice, available, engine);
      case 'hard': return this.decideCategoryHard(dice, player, available, engine);
      default: return available[0];
    }
  }

  /**
   * Decide whether the AI should roll again or score.
   * Returns true if AI should roll again.
   */
  shouldRollAgain(
    dice: Die[],
    rollsRemaining: number,
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): boolean {
    if (rollsRemaining <= 0) return false;

    switch (this.difficulty) {
      case 'easy': return Math.random() > 0.4;
      case 'medium': return this.shouldRollMedium(dice, player, engine);
      case 'hard': return this.shouldRollHard(dice, rollsRemaining, player, engine);
      default: return rollsRemaining > 0;
    }
  }

  // =========================================================================
  // Easy Difficulty
  // =========================================================================

  private decideHoldsEasy(dice: Die[]): number[] {
    // Randomly hold 0-3 dice
    const holdCount = Math.floor(Math.random() * 4);
    const indices = Array.from({ length: NUM_DICE }, (_, i) => i);
    this.shuffleArray(indices);
    return indices.slice(0, holdCount);
  }

  private decideCategoryEasy(available: ScoreCategory[]): ScoreCategory {
    return available[Math.floor(Math.random() * available.length)];
  }

  // =========================================================================
  // Medium Difficulty
  // =========================================================================

  private decideHoldsMedium(
    dice: Die[],
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): number[] {
    const values = dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);

    // Find the most common value
    let maxCount = 0;
    let maxValue = 1;
    for (let v = 1; v <= 6; v++) {
      if (counts[v] > maxCount) {
        maxCount = counts[v];
        maxValue = v;
      }
    }

    // Hold all dice matching the most common value (greedy - going for multiples)
    if (maxCount >= 2) {
      return dice
        .map((d, i) => (d.value === maxValue ? i : -1))
        .filter((i) => i !== -1);
    }

    // Hold highest values for chance
    return dice
      .map((d, i) => (d.value >= 4 ? i : -1))
      .filter((i) => i !== -1);
  }

  private decideCategoryMedium(
    dice: Die[],
    available: ScoreCategory[],
    engine: YahtzeeEngine
  ): ScoreCategory {
    // Pick the category with the highest score
    let bestCategory = available[0];
    let bestScore = -1;

    for (const cat of available) {
      const score = engine.calculateScore(cat, dice);
      if (score > bestScore) {
        bestScore = score;
        bestCategory = cat;
      }
    }

    return bestCategory;
  }

  private shouldRollMedium(
    dice: Die[],
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): boolean {
    const available = engine.getAvailableCategories(player);
    const potentialScores = available.map((cat) => engine.calculateScore(cat, dice));
    const maxPotential = Math.max(...potentialScores, 0);

    // If we have a good score already (>= 25), consider stopping
    if (maxPotential >= 25) return false;

    // If we have a Yahtzee, definitely stop
    const values = dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);
    if (counts.includes(5)) return false;

    return true;
  }

  // =========================================================================
  // Hard Difficulty
  // =========================================================================

  private decideHoldsHard(
    dice: Die[],
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): number[] {
    const values = dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);
    const available = engine.getAvailableCategories(player);

    // Priority 1: If going for Yahtzee and have 3+ of a kind, hold them
    if (available.includes('yahtzee')) {
      for (let v = 6; v >= 1; v--) {
        if (counts[v] >= 3) {
          return dice
            .map((d, i) => (d.value === v ? i : -1))
            .filter((i) => i !== -1);
        }
      }
    }

    // Priority 2: If close to large straight (4 in sequence), hold those
    if (available.includes('largeStraight')) {
      const straightHolds = this.getHoldsForStraight(dice, 5);
      if (straightHolds.length >= 4) return straightHolds;
    }

    // Priority 3: If close to small straight (3 in sequence), hold those
    if (available.includes('smallStraight')) {
      const straightHolds = this.getHoldsForStraight(dice, 4);
      if (straightHolds.length >= 3) return straightHolds;
    }

    // Priority 4: Hold matching dice for multiples (prefer higher values)
    for (let v = 6; v >= 1; v--) {
      if (counts[v] >= 2) {
        // Consider upper section bonus tracking
        const upperTotal = engine.getUpperSectionTotal(player);
        const remaining = UPPER_BONUS_THRESHOLD - upperTotal;

        // Hold matching dice
        return dice
          .map((d, i) => (d.value === v ? i : -1))
          .filter((i) => i !== -1);
      }
    }

    // Priority 5: Hold high-value dice for chance
    return dice
      .map((d, i) => (d.value >= 5 ? i : -1))
      .filter((i) => i !== -1);
  }

  private decideCategoryHard(
    dice: Die[],
    player: YahtzeePlayer,
    available: ScoreCategory[],
    engine: YahtzeeEngine
  ): ScoreCategory {
    const scores: { category: ScoreCategory; score: number; weight: number }[] = [];

    const upperTotal = engine.getUpperSectionTotal(player);
    const needForBonus = UPPER_BONUS_THRESHOLD - upperTotal;
    const upperRemaining = UPPER_CATEGORIES.filter((c) => available.includes(c));
    const canStillGetBonus = needForBonus <= 0 || upperRemaining.length > 0;

    for (const cat of available) {
      const score = engine.calculateScore(cat, dice);
      let weight = score;

      // Weight upper section categories higher if bonus is achievable
      if (UPPER_CATEGORIES.includes(cat as any) && canStillGetBonus && needForBonus > 0) {
        const targetValue = UPPER_CATEGORIES.indexOf(cat as any) + 1;
        const idealScore = targetValue * 3; // 3 dice of that value is the target
        if (score >= idealScore) {
          weight += 10; // Bonus for meeting upper section target
        }
      }

      // Penalize scoring 0 in Yahtzee (lose future bonus potential)
      if (cat === 'yahtzee' && score === 0) {
        weight -= 5;
      }

      // Bonus for scoring Yahtzee
      if (cat === 'yahtzee' && score > 0) {
        weight += 20;
      }

      // Bonus for large straight (rare and high value)
      if (cat === 'largeStraight' && score > 0) {
        weight += 10;
      }

      // Penalize using chance early with low score
      if (cat === 'chance' && score < 20) {
        weight -= 5;
      }

      // If scoring 0, prefer to burn a less valuable category
      if (score === 0) {
        // Prefer to burn ones/twos (less impact on upper bonus)
        if (cat === 'ones') weight += 3;
        else if (cat === 'twos') weight += 2;
        else if (cat === 'largeStraight') weight -= 2; // Don't burn large straight early
      }

      scores.push({ category: cat, score, weight });
    }

    // Sort by weight descending
    scores.sort((a, b) => b.weight - a.weight);
    return scores[0].category;
  }

  private shouldRollHard(
    dice: Die[],
    rollsRemaining: number,
    player: YahtzeePlayer,
    engine: YahtzeeEngine
  ): boolean {
    const values = dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);
    const available = engine.getAvailableCategories(player);

    // Have a Yahtzee? Stop.
    if (counts.includes(5)) return false;

    // Have a large straight? Stop if available.
    if (available.includes('largeStraight')) {
      const unique = [...new Set(values)].sort();
      if (unique.length === 5 && (unique[4] - unique[0] === 4)) return false;
    }

    // Have full house? Stop if it's a good score.
    if (available.includes('fullHouse') && counts.includes(3) && counts.includes(2)) {
      return false;
    }

    // Have four of a kind? Roll for Yahtzee if available.
    if (counts.includes(4) && available.includes('yahtzee')) {
      return true;
    }

    // Have four of a kind but no Yahtzee available? Stop.
    if (counts.includes(4) && !available.includes('yahtzee')) {
      return false;
    }

    // Otherwise, if best potential score is decent, consider stopping on last roll
    if (rollsRemaining === 1) {
      const maxScore = Math.max(
        ...available.map((cat) => engine.calculateScore(cat, dice))
      );
      if (maxScore >= 20) return false;
    }

    return true;
  }

  // =========================================================================
  // Utilities
  // =========================================================================

  private getHoldsForStraight(dice: Die[], targetLength: number): number[] {
    const values = dice.map((d) => d.value);
    const unique = [...new Set(values)].sort((a, b) => a - b);

    // Find longest consecutive sequence
    let bestSequence: number[] = [];
    let currentSequence: number[] = [unique[0]];

    for (let i = 1; i < unique.length; i++) {
      if (unique[i] === unique[i - 1] + 1) {
        currentSequence.push(unique[i]);
      } else {
        if (currentSequence.length > bestSequence.length) {
          bestSequence = [...currentSequence];
        }
        currentSequence = [unique[i]];
      }
    }
    if (currentSequence.length > bestSequence.length) {
      bestSequence = currentSequence;
    }

    if (bestSequence.length < targetLength - 1) return [];

    // Hold dice that are part of the sequence
    const holdIndices: number[] = [];
    const usedValues = new Set<number>();
    for (const v of bestSequence) {
      const idx = dice.findIndex(
        (d, i) => d.value === v && !usedValues.has(i) && !holdIndices.includes(i)
      );
      if (idx !== -1) {
        holdIndices.push(idx);
        usedValues.add(idx);
      }
    }

    return holdIndices;
  }

  private getDiceCounts(values: DieValue[]): number[] {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    for (const v of values) {
      counts[v]++;
    }
    return counts;
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
}
